import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  TrendingUp,
  Shield,
  Zap,
  Lock,
  Activity,
  CheckCircle2,
  ArrowRight,
  Wallet,
  BarChart3,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';

const LandingPage = () => {
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);

  const features = [
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: 'Automated Trading',
      description: 'Set up your bot once and let it trade 24/7 using proven strategies',
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: 'Multiple Strategies',
      description: '6 institutional-grade strategies: Alpha, Momentum, Defensive, Fibonacci, Pattern, Composite',
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Risk Controls',
      description: 'Built-in stop-loss, daily loss limits, and position sizing to protect your capital',
    },
  ];

  const trustIndicators = [
    { icon: <Lock />, text: 'No withdrawal access' },
    { icon: <Wallet />, text: 'Full custody remains with you' },
    { icon: <Activity />, text: 'Paper trade first' },
    { icon: <BarChart3 />, text: 'Real-time P&L tracking' },
  ];

  const pricing = [
    {
      name: 'Core',
      price: 99,
      bots: '1 Bot',
      features: ['1 automated bot', 'Paper trading only', 'Basic analytics', 'Risk controls'],
      tier: 'core',
    },
    {
      name: 'Pro',
      price: 499,
      bots: '3 Bots',
      features: ['3 automated bots', 'Live trading', 'Advanced analytics', 'Priority support'],
      tier: 'pro',
      popular: true,
    },
    {
      name: 'Elite',
      price: 999,
      bots: '6 Bots',
      features: ['6 automated bots', 'Live trading', 'PDF reports', 'Priority strategies'],
      tier: 'elite',
      elite: true,
    },
  ];

  return (
    <div className="min-h-screen bg-[#0B0D12] text-[#F5F7FA]">
      {/* Header */}
      <header className="glass-effect border-b border-[#1E293B] sticky top-0 z-50">
        <div className="container mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="text-2xl font-bold font-['Outfit'] tracking-tight">APEXION</div>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm hover:text-[#2F6BFF] transition-colors">
              Strategies
            </a>
            <a href="#pricing" className="text-sm hover:text-[#2F6BFF] transition-colors">
              Pricing
            </a>
            <Button
              data-testid="header-get-started-btn"
              onClick={() => navigate('/auth')}
              className="bg-[#2F6BFF] hover:bg-[#2F6BFF]/90 rounded-sm"
            >
              Get Started
            </Button>
          </nav>
          {/* Mobile menu button */}
          <Button
            variant="ghost"
            className="md:hidden"
            onClick={() => navigate('/auth')}
          >
            Get Started
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 hero-gradient"></div>
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="text-5xl md:text-7xl font-semibold tracking-tight mb-6 font-['Outfit']">
              Institutional-Grade
              <br />
              <span className="text-[#2F6BFF]">Crypto Automation</span>
            </h1>
            <p className="text-lg md:text-xl text-[#94A3B8] leading-relaxed mb-8 max-w-2xl mx-auto">
              Deploy systematic crypto strategies with professional risk controls — while keeping full custody of your
              funds.
            </p>
            <Button
              data-testid="hero-start-paper-trading-btn"
              onClick={() => navigate('/auth')}
              size="lg"
              className="bg-[#2F6BFF] hover:bg-[#2F6BFF]/90 rounded-sm text-lg px-8 shadow-[0_0_20px_-5px_rgba(47,107,255,0.5)]"
            >
              Start Trading <ArrowRight className="ml-2" />
            </Button>
          </motion.div>

          {/* Trust Indicators */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto"
          >
            {trustIndicators.map((item, idx) => (
              <div key={idx} className="flex flex-col items-center gap-2 text-center">
                <div className="text-[#2F6BFF]">{item.icon}</div>
                <p className="text-sm text-[#94A3B8]">{item.text}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 md:py-32 bg-[#15171C]">
        <div className="container mx-auto px-4 md:px-8">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-4 font-['Outfit']">How It Works</h2>
            <p className="text-[#94A3B8] max-w-2xl mx-auto">Four simple steps to automated trading</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {[
              { num: '01', title: 'Sign Up & Subscribe', desc: 'Create account, choose your plan' },
              { num: '02', title: 'Connect Exchange', desc: 'Add API keys (trade-only, no withdrawal access)' },
              { num: '03', title: 'Choose Strategy', desc: 'Pick from 6 proven trading strategies' },
              { num: '04', title: 'Profit on Your Exchange', desc: 'Profits stay in your exchange — withdraw anytime' },
            ].map((step, idx) => (
              <motion.div
                key={idx}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-[#0B0D12] border border-[#1E293B] rounded-sm p-6 hover:border-[#2F6BFF]/50 transition-colors"
              >
                <div className="text-4xl font-bold text-[#2F6BFF] mb-4 font-['JetBrains_Mono']">{step.num}</div>
                <h3 className="text-xl font-semibold mb-2 font-['Outfit']">{step.title}</h3>
                <p className="text-[#94A3B8] text-sm">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Strategies */}
      <section id="features" className="py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-8">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-4 font-['Outfit']">
              Orbis Strategy Line
            </h2>
            <p className="text-[#94A3B8] max-w-2xl mx-auto">Fully systematic, rules-based strategies</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {features.map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className="bg-[#15171C] border-[#1E293B] hover:border-[#2F6BFF]/50 transition-colors h-full">
                  <CardHeader>
                    <div className="text-[#2F6BFF] mb-4">{feature.icon}</div>
                    <CardTitle className="font-['Outfit']">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-[#94A3B8]">{feature.description}</CardDescription>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 md:py-32 bg-[#15171C]">
        <div className="container mx-auto px-4 md:px-8">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-4 font-['Outfit']">Simple Pricing</h2>
            <p className="text-[#94A3B8] max-w-2xl mx-auto">Choose based on how many bots you want to run</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricing.map((plan, idx) => (
              <motion.div
                key={idx}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className={`relative ${plan.popular ? 'md:-mt-4' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#2F6BFF] text-white px-4 py-1 rounded-sm text-sm font-medium">
                    Most Popular
                  </div>
                )}
                <Card
                  className={`h-full ${
                    plan.elite
                      ? 'bg-gradient-to-br from-[#15171C] to-[#1E293B] border-[#C9A24D]'
                      : 'bg-[#15171C] border-[#1E293B]'
                  } ${plan.popular ? 'border-[#2F6BFF]' : ''}`}
                >
                  <CardHeader>
                    <CardTitle className={`font-['Outfit'] text-2xl ${plan.elite ? 'text-[#C9A24D]' : ''}`}>
                      {plan.name}
                    </CardTitle>
                    <p className="text-[#2F6BFF] font-semibold">{plan.bots}</p>
                    <div className="mt-4">
                      <>
                          <span className="text-5xl font-bold font-['JetBrains_Mono']">£{plan.price}</span>
                          <span className="text-[#94A3B8]">/mo</span>
                        </>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3 mb-6">
                      {plan.features.map((feature, fidx) => (
                        <li key={fidx} className="flex items-start gap-2">
                          <CheckCircle2 className="w-5 h-5 text-[#2F6BFF] mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-[#94A3B8]">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      data-testid={`pricing-${plan.tier}-btn`}
                      onClick={() => navigate('/auth')}
                      className={`w-full rounded-sm ${
                        plan.elite
                          ? 'bg-[#C9A24D] hover:bg-[#C9A24D]/90 text-[#0B0D12]'
                          : plan.popular
                          ? 'bg-[#2F6BFF] hover:bg-[#2F6BFF]/90'
                          : 'bg-[#1E293B] hover:bg-[#1E293B]/90'
                      }`}
                    >
                      Get Started
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* No Refunds Policy Notice */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0B0D12] border border-[#1E293B]">
              <AlertTriangle className="w-4 h-4 text-yellow-400" />
              <span className="text-sm text-[#94A3B8]">
                All purchases are final. No refunds. Cancel anytime — access continues until billing period ends.
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Legal Disclaimers */}
      <section className="py-16 bg-[#0B0D12] border-t border-[#1E293B]">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex items-start gap-4 max-w-4xl mx-auto">
            <AlertTriangle className="w-5 h-5 text-[#EF4444] flex-shrink-0 mt-1" />
            <div className="text-sm text-[#94A3B8] space-y-2">
              <p>
                <strong>Trading involves risk.</strong> Past performance does not guarantee future results.
              </p>
              <p>Apexion does not provide financial advice. Apexion does not have custody of user funds.</p>
              <p>Users retain full control and can stop automation at any time.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[#15171C] border-t border-[#1E293B]">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="text-2xl font-bold font-['Outfit'] mb-4">APEXION</div>
              <p className="text-sm text-[#94A3B8]">Institutional-Grade Crypto Automation</p>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Product</h3>
              <ul className="space-y-2 text-sm text-[#94A3B8]">
                <li><a href="#features" className="hover:text-[#2F6BFF] transition-colors">Strategies</a></li>
                <li><a href="#pricing" className="hover:text-[#2F6BFF] transition-colors">Pricing</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Support</h3>
              <ul className="space-y-2 text-sm text-[#94A3B8]">
                <li>
                  <a href="mailto:support@apexion.uk" className="hover:text-[#2F6BFF] transition-colors">
                    support@apexion.uk
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="text-center border-t border-[#1E293B] pt-8">
            <p className="text-xs text-[#94A3B8]">© 2025 Apexion. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;